import { useMemo, useCallback } from 'react'
import {
  useUpdate,
} from 'react-admin'
import {
  capitalize,
  Grid,
  Link,
  Typography,
  Button,
} from '@mui/material'
import { useNotifyResponse, useTranslateResource, useSettings } from '../../../hooks'
import {
  acceptanceStatusTypes,
  completionStatusTypes,
  cancelConfirmStatusTypes,
  suspendedConfirmStatusTypes,
} from './util'

const Acceptance = ({ record, resource }) => {
  const { id, hauler_acceptance_status } = record
  const translateDispatch = useTranslateResource('dispatches')
  const translate = useTranslateResource(resource)
  const { isEmployeeDriver } = useSettings()

  const resourceName = useMemo(() => {
    return capitalize(translate('name', { smart_count: 1 }))
  }, [translate])

  const [update, { isLoading: isSubmittingAcceptanceStatus }] = useUpdate()
  const { notifySuccess, notifyFailure } = useNotifyResponse({ resourceName })

  const updateAcceptanceStatus = useCallback(async status => {
    try {
      await update(resource, {
        id,
        data: { hauler_acceptance_status: status },
      }, {
        returnPromise: true,
      })
      notifySuccess(isEmployeeDriver ? 'confirmed' : status)
    } catch (error) {
      notifyFailure(error)
      if (error.body.attributeErrors) {
        return error.body.attributeErrors
      }
    }
  }, [update, notifyFailure, notifySuccess, isEmployeeDriver, resource, id])

  // Employee driver logic - can only confirm (accept)
  if (isEmployeeDriver) {
    const {
      Icon,
      iconColor,
      buttonColor,
    } = acceptanceStatusTypes.accepted

    if (hauler_acceptance_status === 'accepted') {
      return (
        <Grid container columnSpacing={0.8} alignItems='center'>
          <Grid item display='flex'>
            <Icon fontSize='small' color={iconColor} />
          </Grid>
          <Grid item>
            <Typography variant='body2'>
              {translateDispatch('form.confirmedMessage')}
            </Typography>
          </Grid>
        </Grid>
      )
    } else {
      return (
        <Grid container spacing={2}>
          <Grid item>
            <Button
              variant='contained'
              color={buttonColor}
              size='small'
              disabled={isSubmittingAcceptanceStatus}
              onClick={updateAcceptanceStatus.bind(null, 'accepted')}
            >
              {translateDispatch('form.confirmButton')}
            </Button>
          </Grid>
        </Grid>
      )
    }
  }

  // Broker driver logic - accept / decline
  const {
    Icon,
    iconColor,
    statusMessage,
    changeAction,
  } = acceptanceStatusTypes[hauler_acceptance_status] || {}

  if (hauler_acceptance_status) {
    return (
      <Grid container columnSpacing={0.8} alignItems='center'>
        <Grid item display='flex'>
          <Icon fontSize='small' color={iconColor} />
        </Grid>
        <Grid item>
          <Typography variant='body2'>
            {translateDispatch(statusMessage)}
          </Typography>
        </Grid>
        <Grid item display='flex'>
          <Link
            variant='body2'
            component='button'
            color='secondary'
            sx={{ verticalAlign: 'inherit' }}
            onClick={updateAcceptanceStatus.bind(null, hauler_acceptance_status === 'accepted' ? 'declined' : 'accepted')}
          >
            {translateDispatch(changeAction)}
          </Link>
        </Grid>
      </Grid>
    )
  } else {
    return (
      <Grid container spacing={2}>
        {
          Object.keys(acceptanceStatusTypes).map(key => {
            return (
              <Grid item display='flex' key={key}>
                <Button
                  variant='contained'
                  color={acceptanceStatusTypes[key].buttonColor}
                  size='small'
                  disabled={isSubmittingAcceptanceStatus}
                  onClick={updateAcceptanceStatus.bind(null, key)}
                >
                  {translateDispatch(acceptanceStatusTypes[key].buttonText)}
                </Button>
              </Grid>
            )
          })
        }
      </Grid>
    )
  }
}

const Completion = ({ record, resource }) => {
  const { id, is_completed } = record
  const translateDispatch = useTranslateResource('dispatches')
  const translate = useTranslateResource(resource)

  const resourceName = useMemo(() => {
    return capitalize(translate('name', { smart_count: 1 }))
  }, [translate])

  const [update, { isLoading: isSubmittingCompletion }] = useUpdate()
  const { notifySuccess, notifyFailure } = useNotifyResponse({ resourceName })

  const updateCompletion = useCallback(async completed => {
    try {
      await update(resource, {
        id,
        data: { is_completed: completed },
      }, {
        returnPromise: true,
      })
      notifySuccess(completed ? 'completed' : 'completionUndone')
    } catch (error) {
      notifyFailure(error)
      if (error.body.attributeErrors) {
        return error.body.attributeErrors
      }
    }
  }, [update, notifyFailure, notifySuccess, resource, id])

  const {
    Icon,
    iconColor,
    buttonColor,
    buttonText,
    statusMessage,
    changeAction,
  } = completionStatusTypes[is_completed]

  if (is_completed) {
    return (
      <Grid container columnSpacing={0.8} alignItems='center'>
        <Grid item display='flex'>
          <Icon fontSize='small' color={iconColor} />
        </Grid>
        <Grid item display='flex'>
          <Typography variant='body2'>
            {translateDispatch(statusMessage)}
          </Typography>
        </Grid>
        <Grid item display='flex'>
          <Link
            variant='body2'
            component='button'
            color='secondary'
            sx={{ verticalAlign: 'inherit' }}
            onClick={updateCompletion.bind(null, false)}
          >
            {translateDispatch(changeAction)}
          </Link>
        </Grid>
      </Grid>
    )
  } else {
    return (
      <Grid container spacing={2}>
        <Grid item display='flex'>
          <Button
            variant='outlined'
            color={buttonColor}
            size='small'
            startIcon={<Icon fontSize='small' />}
            disabled={isSubmittingCompletion}
            onClick={updateCompletion.bind(null, true)}
          >
            {translateDispatch(buttonText)}
          </Button>
        </Grid>
      </Grid>
    )
  }
}

const CancelConfirmation = ({ record, resource }) => {
  const { id, is_cancel_confirmed } = record
  const translateDispatch = useTranslateResource('dispatches')
  const translate = useTranslateResource(resource)

  const resourceName = useMemo(() => {
    return capitalize(translate('name', { smart_count: 1 }))
  }, [translate])

  const [update, { isLoading: isSubmittingCancelConfirm }] = useUpdate()
  const { notifySuccess, notifyFailure } = useNotifyResponse({ resourceName })

  const updateCancelConfirm = useCallback(async () => {
    try {
      await update(resource, {
        id,
        data: { is_cancel_confirmed: true },
      }, {
        returnPromise: true,
      })
      notifySuccess('cancelConfirmed')
    } catch (error) {
      notifyFailure(error)
      if (error.body.attributeErrors) {
        return error.body.attributeErrors
      }
    }
  }, [update, notifyFailure, notifySuccess, resource, id])

  const {
    Icon,
    color,
    statusMessage,
    buttonText,
  } = cancelConfirmStatusTypes[is_cancel_confirmed] || {}

  return (
    <Grid container columnSpacing={0.8} alignItems='center'>
      <Grid item display='flex'>
        <Icon fontSize='small' color={color} />
      </Grid>
      <Grid item display='flex'>
        <Typography variant='body2'>
          {translateDispatch(statusMessage)}
        </Typography>
      </Grid>
      {
        buttonText &&
        <Grid item display='flex'>
          <Link
            variant='body2'
            component='button'
            color='secondary'
            disabled={isSubmittingCancelConfirm}
            sx={{ verticalAlign: 'inherit' }}
            onClick={updateCancelConfirm}
          >
            {translateDispatch(buttonText)}
          </Link>
        </Grid>
      }
    </Grid>
  )
}

const Suspended = ({ record, resource }) => {
  const { id, is_suspend_confirmed, hauler_acceptance_status } = record
  const translateDispatch = useTranslateResource('dispatches')
  const translate = useTranslateResource(resource)

  const resourceName = useMemo(() => {
    return capitalize(translate('name', { smart_count: 1 }))
  }, [translate])

  const [update, { isLoading: isSubmittingSuspendedConfirm }] = useUpdate()
  const { notifySuccess, notifyFailure } = useNotifyResponse({ resourceName })

  const updateSuspendedConfirm = useCallback(async () => {
    try {
      await update(resource, {
        id,
        data: { is_suspend_confirmed: true },
      }, {
        returnPromise: true,
      })
      notifySuccess('suspendConfirmed')
    } catch (error) {
      notifyFailure(error)
      if (error.body.attributeErrors) {
        return error.body.attributeErrors
      }
    }
  }, [update, notifyFailure, notifySuccess, resource, id])

  const {
    Icon,
    color,
    statusMessage,
    buttonText,
  } = suspendedConfirmStatusTypes[is_suspend_confirmed] || {}

  return (
    <Grid container columnSpacing={0.8} alignItems='center'>
      <Grid item display='flex'>
        <Icon fontSize='small' color={color} />
      </Grid>
      <Grid item>
        <Typography variant='body2'>
          {translateDispatch(statusMessage)}
        </Typography>
      </Grid>
      {
        buttonText && hauler_acceptance_status === 'accepted' &&
        <Grid item display='flex'>
          <Link
            variant='body2'
            component='button'
            color='secondary'
            disabled={isSubmittingSuspendedConfirm}
            sx={{ verticalAlign: 'inherit' }}
            onClick={updateSuspendedConfirm}
          >
            {translateDispatch(buttonText)}
          </Link>
        </Grid>
      }
    </Grid>
  )
}

const Component = props => {
  const { record: { is_canceled, is_suspended, is_completed, hauler_acceptance_status } }= props

  if (is_canceled) {
    return <CancelConfirmation {...props} />
  } else {
    return (
      <Grid container direction='column' spacing={1.4}>
        {
          hauler_acceptance_status === 'accepted' &&
          <Grid item>
            <Completion {...props} />
          </Grid>
        }
        {
          !is_completed &&
          <Grid item>
            <Acceptance {...props} />
          </Grid>
        }
        {
          is_suspended &&
          <Grid item>
            <Suspended {...props} />
          </Grid>
        }
      </Grid>
    )
  }
}

export default Component
